import React from 'react';
import { Heart, Github, Linkedin, Mail, ArrowUp } from 'lucide-react';
import { useLanguage } from '../LanguageContext';
import { useTheme } from '../ThemeContext';
import { useData } from '../DataContext';

const Footer = () => {
  const { t } = useLanguage();
  const { isDark } = useTheme();
  const { contact } = useData();
  const currentYear = new Date().getFullYear();

  const social = contact.data?.social || {};

  const quickLinks = [
    { nameKey: 'home', href: '#home' },
    { nameKey: 'about', href: '#about' },
    { nameKey: 'projects', href: '#projects' },
    { nameKey: 'contact', href: '#contact' }
  ];

  const socialLinks = [
    { icon: <Github className="w-5 h-5" />, name: 'GitHub', url: social.github || '#' },
    { icon: <Linkedin className="w-5 h-5" />, name: 'LinkedIn', url: social.linkedin || '#' },
    { icon: <Mail className="w-5 h-5" />, name: 'Email', url: `mailto:${contact.data?.email || 'salih@ai-engineer.com'}` }
  ];

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <footer className={`border-t relative overflow-hidden ${isDark ? 'bg-[#0a0f1a] border-white/5' : 'bg-slate-50 border-slate-200'}`}>
      {/* Background Elements */}
      <div className="absolute inset-0">
        <div className={`absolute bottom-0 left-1/4 w-96 h-96 ${isDark ? 'bg-blue-500/5' : 'bg-blue-200/20'} rounded-full blur-[120px]`}></div>
      </div>

      <div className="container mx-auto px-6 relative z-10">
        {/* Main Footer Content */}
        <div className="py-16">
          <div className="grid lg:grid-cols-4 gap-12">
            {/* Brand Section */}
            <div className="lg:col-span-1">
              <a href="#home" className="group flex items-center gap-3 mb-6">
                <div className="w-1 h-12 bg-blue-500 rounded-full"></div>
                <div className="flex flex-col leading-tight">
                  <span className={`text-lg font-bold tracking-wide group-hover:text-blue-400 transition-colors duration-300 ${isDark ? 'text-white' : 'text-slate-800'}`}>
                    Muhammed Salih
                  </span>
                  <span className={`text-lg font-bold tracking-wide group-hover:text-blue-400 transition-colors duration-300 ${isDark ? 'text-white' : 'text-slate-800'}`}>
                    DEDE
                  </span>
                </div>
              </a>
              <p className={`mb-6 leading-relaxed font-light ${isDark ? 'text-gray-500' : 'text-gray-600'}`}>
                {t.footer.description}
              </p>
              <div className="flex space-x-3">
                {socialLinks.map((social, index) => (
                  <a
                    key={index}
                    href={social.url}
                    className={`p-3 rounded-xl transition-all duration-300 hover:scale-110 ${
                      isDark
                        ? 'bg-white/5 text-gray-400 hover:text-white hover:bg-blue-500/20'
                        : 'bg-slate-100 text-slate-600 hover:text-white hover:bg-blue-500'
                    }`}
                    title={social.name}
                  >
                    {social.icon}
                  </a>
                ))}
              </div>
            </div>

            {/* Quick Links */}
            <div>
              <h3 className={`font-semibold mb-6 ${isDark ? 'text-white' : 'text-slate-800'}`}>{t.footer.quickLinks}</h3>
              <ul className="space-y-3">
                {quickLinks.map((link, index) => (
                  <li key={index}>
                    <a
                      href={link.href}
                      className={`transition-colors duration-300 font-light ${
                        isDark ? 'text-gray-500 hover:text-blue-400' : 'text-gray-600 hover:text-blue-600'
                      }`}
                    >
                      {t.nav[link.nameKey]}
                    </a>
                  </li>
                ))}
              </ul>
            </div>

            {/* Expertise */}
            <div>
              <h3 className={`font-semibold mb-6 ${isDark ? 'text-white' : 'text-slate-800'}`}>{t.footer.expertise}</h3>
              <ul className="space-y-3">
                {t.footer.expertiseAreas.map((area, index) => (
                  <li key={index}>
                    <span className={`transition-colors duration-300 font-light cursor-default ${
                      isDark ? 'text-gray-500 hover:text-blue-400' : 'text-gray-600 hover:text-blue-600'
                    }`}>{area}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Newsletter / CTA */}
            <div>
              <h3 className={`font-semibold mb-6 ${isDark ? 'text-white' : 'text-slate-800'}`}>{t.footer.startProject}</h3>
              <p className={`mb-6 font-light ${isDark ? 'text-gray-500' : 'text-gray-600'}`}>
                {t.footer.startProjectDesc}
              </p>
              <a
                href="#contact"
                className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-xl font-medium hover:shadow-lg hover:shadow-blue-500/25 transition-all duration-300"
              >
                {t.footer.getInTouch}
              </a>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className={`py-6 border-t ${isDark ? 'border-white/5' : 'border-slate-200'}`}>
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <p className={`text-sm font-light ${isDark ? 'text-gray-500' : 'text-gray-600'}`}>
              © {currentYear} Muhammed Salih DEDE. {t.footer.rights}
            </p>
            <p className={`text-sm font-light flex items-center gap-1 ${isDark ? 'text-gray-500' : 'text-gray-600'}`}>
              {t.footer.builtWith} <Heart className="w-4 h-4 text-red-500 inline" /> React & Tailwind CSS
            </p>
            <button
              onClick={scrollToTop}
              className={`p-3 rounded-xl transition-all duration-300 hover:scale-110 ${
                isDark
                  ? 'bg-white/5 text-gray-400 hover:text-white hover:bg-blue-500/20'
                  : 'bg-slate-100 text-slate-600 hover:text-white hover:bg-blue-500'
              }`}
              title="Back to top"
            >
              <ArrowUp className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
